import type { JSX } from 'react';
import CustomTypography from '@/components/Common/Typography';
import type {
  ChartAssay,
  DiseaseBurdenFilters,
  IChartLaneLogScale,
  IDiseaseBurdenData,
  InformedMode,
} from '@/types/PatientDashboard/PatientTimeline.types';
import AnalyteRow from './AnalyteRow';
import { ChartLaneEmpty } from './ChartLane.styles';
import LaneError from './LaneError';
import ChartLaneLoading from './ChartLaneLoading';

interface IProps {
  data: IDiseaseBurdenData;
  filters: DiseaseBurdenFilters;
  hiddenAnalytes: Record<string, boolean>;
  isLoading: boolean;
  hasFailure: boolean;
  scale: IChartLaneLogScale;
  onToggleAssay: (analyte: string, assay: ChartAssay) => void;
  onSetMode: (analyte: string, assay: ChartAssay, mode: InformedMode) => void;
  onToggleVisibility: (analyte: string) => void;
  onRetry: () => void;
  xPct: (date: string) => number;
}

export default function DiseaseBurdenLaneBody({
  data,
  filters,
  hiddenAnalytes,
  isLoading,
  hasFailure,
  scale,
  onToggleAssay,
  onSetMode,
  onToggleVisibility,
  onRetry,
  xPct,
}: IProps): JSX.Element {
  if (isLoading) {
    return <ChartLaneLoading />;
  }

  if (hasFailure && !data.analytes.length) {
    return <LaneError lane="chart" message="Could not load disease burden data." onRetry={onRetry} />;
  }

  if (!data.analytes.length) {
    return (
      <ChartLaneEmpty>
        <CustomTypography variant="bodySmall">
          No disease burden results for this patient.
        </CustomTypography>
      </ChartLaneEmpty>
    );
  }

  return (
    <>
      {data.analytes.map((analyte) => (
        <AnalyteRow
          key={analyte.analyte}
          analyte={analyte}
          filters={filters[analyte.analyte]}
          isVisible={!hiddenAnalytes[analyte.analyte]}
          scale={scale}
          onToggleAssay={onToggleAssay}
          onSetMode={onSetMode}
          onToggleVisibility={onToggleVisibility}
          xPct={xPct}
        />
      ))}
    </>
  );
}
